import {
  MembershipRewashEligibilityState,
  OrderRequestHeaderWashRef,
  OrderRequestHistoryActionKind,
  OrderRequestHistoryInsightBuildResult,
  OrderRequestHistoryRow,
  OrderRequestRewashTarget,
  OrderRequestTodayBannerState,
  PrepaidRewashEligibilityState,
  RewashEligibilityState,
} from '../models/notification-order-request.models';
import { OrderUtils } from '../../../orders/utils/order.utils';
import { getDefaultShopTimezone, getUserTimezone } from '../../../shared/utils/date.util';

/**
 * CWS-1882 ("one sale = one order"): redeeming a prepaid wash creates no order, so the
 * vehicle history returns the visit as its own row kind. The row JOINs the PURCHASE order
 * (`o.*`), so its `wash_type` reads `prepaid_wash_book` and its `order_status` `completed` —
 * neither describes the wash itself. Redemptions made before that change still arrive as
 * ordinary `order` rows with `wash_type: 'prepaid_wash_redemption'`.
 */
export const PREPAID_REDEMPTION_HISTORY_TYPE = 'prepaid_redemption';

export function isPrepaidRedemptionHistoryType(historyType: string | null | undefined): boolean {
  return String(historyType ?? '').trim() === PREPAID_REDEMPTION_HISTORY_TYPE;
}

export function formatWashTypeLabel(washType: string): string {
  return washType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Wash Type column labels for vehicle history table membership rows. */
export function formatHistoryTableWashTypeLabel(
  washTypeValue: string,
  historyType: string,
  washCountPeriod?: string | null,
  washCountFrequency?: number | string | null,
  prepaidBookName?: string | null
): string {
  const limitLike = {
    wash_count_period: washCountPeriod,
    wash_count_frequency: washCountFrequency,
  };
  // A prepaid visit's own wash type is "a wash off this book" — the joined purchase
  // order's `prepaid_wash_book` would read as a book sale instead (CWS-1882).
  if (isPrepaidRedemptionHistoryType(historyType)) {
    const book = String(prepaidBookName ?? '').trim();
    return book ? book.replace(/_/g, ' ') : 'Prepaid Wash';
  }
  if (historyType === 'subscription_wash' || isMembershipPurchaseWashType(washTypeValue)) {
    return OrderUtils.formatMembershipWashTypeLine(washCountPeriod, washCountFrequency);
  }
  if (OrderUtils.isMonthlyMembershipWashType(washTypeValue)) {
    return OrderUtils.formatStaffOrderWashType(washTypeValue, limitLike);
  }
  return formatWashTypeLabel(washTypeValue);
}

/**
 * Wash Type for the insight card and the same-day sentence above the table. A prepaid visit
 * JOINs the PURCHASE order, so its raw `wash_type` is `prepaid_wash_book` — the book's sale,
 * not the wash drawn (CWS-1882). The table column already makes that split; this applies the
 * same rule where the sentence and the card read the row directly.
 */
export function formatInsightWashTypeLabel(row: any): string {
  if (isPrepaidRedemptionHistoryType(row?.history_type)) {
    const book = String(row?.prepaid_wash_book_name ?? '').trim();
    return book ? book.replace(/_/g, ' ') : 'Prepaid Wash';
  }
  return formatWashTypeLabel((row?.wash_type && String(row.wash_type)) || 'single_wash');
}

/**
 * A wash taken on a plan the customer already holds. The vehicle history lists
 * it beside real orders, and it carries the membership order's $0.00, so without
 * this it reads as a purchase the customer made today.
 */
function isMembershipRedemptionRow(row: any): boolean {
  return String(row?.history_type ?? '').trim() === 'subscription_wash';
}

function isMembershipPurchaseWashType(washTypeValue: string): boolean {
  const value = String(washTypeValue ?? '')
    .trim()
    .toLowerCase();
  if (!value || OrderUtils.isRewashWashType(value)) {
    return false;
  }
  return value.includes('membership');
}

/** Package label for vehicle order history (insight card + table). */
export function formatVehicleHistoryPackageName(o: any): string {
  const washType = String(o?.wash_type ?? '').trim();
  let raw: string | undefined | null;

  if (isPrepaidRedemptionHistoryType(o?.history_type)) {
    // The wash that was drawn, not the book it came from (the book names the Wash Type
    // column) — same split a subscription_wash row makes between plan and wash.
    raw = o?.package_name ?? o?.prepaid_package_name ?? o?.prepaid_wash_book_name;
  } else if (washType === 'prepaid_wash_redemption') {
    raw = o?.prepaid_package_name ?? o?.package_name ?? o?.way_package_name;
  } else if (washType === 'prepaid_wash_book') {
    raw =
      o?.prepaid_wash_book_name ??
      o?.prepaid_package_name ??
      o?.package_name ??
      o?.way_package_name;
  } else {
    // Way redemptions often have no local package_id; name lives on way_package_name.
    raw = o?.package_name ?? o?.way_package_name ?? o?.prepaid_package_name;
  }

  if (raw == null || String(raw).trim() === '') {
    return '—';
  }
  return String(raw).replace(/_/g, ' ');
}

/**
 * Short date for history table rows (e.g. Apr 10, 2026).
 * Admin: default shop timezone from shop_location_state.
 * Staff: falls back to getUserTimezone() → userData.timezone when no shop state.
 */
export function formatInsightVisitDate(value: string | undefined | null): string {
  if (!value) {
    return '';
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    return String(value);
  }
  const timeZone = getDefaultShopTimezone() || getUserTimezone();
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(d);
  } catch {
    return d.toLocaleDateString('en-US', options);
  }
}

/** Header last-visit line: `10 Apr 2026, 11:30 AM` (shop/user timezone). */
export function formatInsightLastVisitDateTime(value: string | undefined | null): string {
  if (!value) {
    return '';
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  const timeZone = getDefaultShopTimezone() || getUserTimezone();
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).formatToParts(d);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find(part => part.type === type)?.value ?? '';
    const day = get('day');
    const month = get('month');
    const year = get('year');
    const hour = get('hour');
    const minute = get('minute');
    const dayPeriod = get('dayPeriod').toUpperCase();
    if (!day || !month || !year) {
      return new Intl.DateTimeFormat('en-US', {
        ...options,
        ...(timeZone ? { timeZone } : {}),
      }).format(d);
    }
    return `${day} ${month} ${year}, ${hour}:${minute} ${dayPeriod}`;
  } catch {
    return d.toLocaleString('en-US', options);
  }
}

/** Inclusive item range label for design pagination, e.g. `Items 1 - 10 of 256`. */
export function formatHistoryItemsRange(page: number, limit: number, total: number): string {
  if (!total || total < 1) {
    return 'Items 0 - 0 of 0';
  }
  const safePage = Math.max(1, page);
  const safeLimit = Math.max(1, limit);
  const start = (safePage - 1) * safeLimit + 1;
  const end = Math.min(safePage * safeLimit, total);
  return `Items ${start} - ${end} of ${total}`;
}

/** Design copy time for `Rewash Available · Until {time}` (shop/user timezone). */
export function formatRewashAvailableUntilTime(expiresAt: string | null | undefined): string {
  if (!expiresAt) {
    return '';
  }
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const timeZone = getDefaultShopTimezone() || getUserTimezone();
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  };
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      ...(timeZone ? { timeZone } : {}),
    }).format(d);
  } catch {
    return d.toLocaleTimeString('en-US', options);
  }
}

/** Vehicle history rows whose event (or created) time falls on the local calendar day. */
export function getTodayVehicleHistoryRows(rows: any[]): any[] {
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayEnd = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59,
    999
  );

  return rows.filter(row => {
    const raw = row?.event_date || row?.created_at;
    if (!raw) {
      return false;
    }
    const d = new Date(raw);
    return d >= todayStart && d <= todayEnd;
  });
}

function asNullableId(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  return text || null;
}

/** Normalize API `feedback_rating` to 1–5 or null. */
export function parseFeedbackRating(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating < 1 || rating > 5) {
    return null;
  }
  return Math.trunc(rating);
}

export function mapVehicleHistoryRows(
  rows: any[],
  formatPackage: (o: any) => string
): OrderRequestHistoryRow[] {
  return rows.map((o: any) => {
    const washTypeValue = (o.wash_type && String(o.wash_type)) || 'single_wash';
    const historyType = (o.history_type && String(o.history_type)) || 'order';
    return {
      packageName: formatPackage(o),
      washType: formatHistoryTableWashTypeLabel(
        washTypeValue,
        historyType,
        o.package_wash_count_period ?? o.wash_count_period,
        o.package_wash_count_frequency ?? o.wash_count_frequency,
        o.prepaid_wash_book_name
      ),
      visitDate: formatInsightVisitDate(o.event_date || o.appointment_time || o.created_at) || '—',
      plate: (o.license_plate && String(o.license_plate).trim()) || '—',
      historyType,
      historyId: asNullableId(o.history_id ?? o.id),
      subscriptionWashId: asNullableId(o.subscription_wash_id),
      prepaidRedemptionId: asNullableId(o.prepaid_redemption_id),
      prepaidVisitId: asNullableId(o.prepaid_visit_id),
      washTypeValue,
      orderStatus: asNullableId(o.order_status),
    };
  });
}

export function buildHeaderWashRefFromApiRow(o: any): OrderRequestHeaderWashRef {
  const washTypeValue = (o?.wash_type && String(o.wash_type)) || 'single_wash';
  return {
    historyType: (o?.history_type && String(o.history_type)) || 'order',
    historyId: asNullableId(o?.history_id ?? o?.id),
    subscriptionWashId: asNullableId(o?.subscription_wash_id),
    prepaidRedemptionId: asNullableId(o?.prepaid_redemption_id),
    prepaidVisitId: asNullableId(o?.prepaid_visit_id),
    washTypeValue,
    orderStatus: asNullableId(o?.order_status),
  };
}

export function buildVehicleHistoryPagination(
  rows: any[],
  response: any,
  page: number,
  pageLimit: number
): { page: number; limit: number; total: number; totalPages: number } {
  const pg = response.pagination;
  const totalFromPg = Number(pg?.total);
  const totalPagesFromPg = Number(pg?.totalPages);
  const limit = Number(pg?.limit) || pageLimit;
  if (pg && typeof pg === 'object') {
    return {
      page: Number(pg.page) || page,
      limit,
      total: Number.isFinite(totalFromPg) ? totalFromPg : rows.length,
      totalPages: Number.isFinite(totalPagesFromPg)
        ? Math.max(1, totalPagesFromPg)
        : Math.max(
            1,
            Math.ceil((Number.isFinite(totalFromPg) ? totalFromPg : rows.length) / limit)
          ),
    };
  }
  return {
    page: 1,
    limit: rows.length || limit,
    total: rows.length,
    totalPages: 1,
  };
}

export function parseRewashEligibilityFromHistoryResponse(
  response: any
): RewashEligibilityState | null {
  const raw = response?.rewash_eligibility;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return {
    eligible: raw.eligible,
    original_order_id: raw.original_order_id,
    anchor_at: raw.anchor_at,
    expires_at: raw.expires_at ?? null,
    remaining_minutes: raw.remaining_minutes ?? null,
  };
}

export function parseMembershipRewashEligibilityFromHistoryResponse(
  response: any
): MembershipRewashEligibilityState | null {
  const raw = response?.membership_rewash_eligibility;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const candidates = Array.isArray(raw.candidates)
    ? raw.candidates
        .filter(
          (c: any) => c && typeof c === 'object' && asNullableId(c.original_subscription_wash_id)
        )
        .map((c: any) => ({
          original_subscription_wash_id: String(c.original_subscription_wash_id).trim(),
          original_order_id: c.original_order_id,
          subscription_id: c.subscription_id,
          package_id: c.package_id,
          package_name: c.package_name ?? null,
          anchor_at: c.anchor_at,
          expires_at: c.expires_at ?? null,
          remaining_minutes: c.remaining_minutes ?? null,
        }))
    : [];
  return {
    eligible: raw.eligible === true && candidates.length > 0,
    candidates,
  };
}

/**
 * CWS-1882: prepaid rewash candidates sit next to the membership ones and are keyed by the
 * `prepaid_redemptions` row id, because a redeemed prepaid wash has no order of its own.
 */
export function parsePrepaidRewashEligibilityFromHistoryResponse(
  response: any
): PrepaidRewashEligibilityState | null {
  const raw = response?.prepaid_rewash_eligibility;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const candidates = Array.isArray(raw.candidates)
    ? raw.candidates
        .filter(
          (c: any) => c && typeof c === 'object' && asNullableId(c.original_prepaid_redemption_id)
        )
        .map((c: any) => ({
          original_prepaid_redemption_id: String(c.original_prepaid_redemption_id).trim(),
          visit_id: c.visit_id,
          original_order_id: c.original_order_id,
          prepaid_purchase_id: c.prepaid_purchase_id,
          package_id: c.package_id,
          package_name: c.package_name ?? null,
          customer_id: c.customer_id,
          anchor_at: c.anchor_at,
          expires_at: c.expires_at ?? null,
          remaining_minutes: c.remaining_minutes ?? null,
        }))
    : [];
  return {
    eligible: raw.eligible === true && candidates.length > 0,
    candidates,
  };
}

/**
 * Drop a subscription_wash row from a vehicle-history API response (context-exclude
 * for the redemption that opened the informational Active subscription modal).
 */
export function omitSubscriptionWashFromHistoryResponse(
  response: any,
  subscriptionWashId: string | null | undefined
): any {
  const washId = asNullableId(subscriptionWashId);
  if (!washId || !response || typeof response !== 'object') {
    return response;
  }

  const rows = Array.isArray(response.data) ? response.data : null;
  if (!rows) {
    return response;
  }

  const filtered = rows.filter((row: any) => {
    const historyType = String(row?.history_type ?? '').trim();
    if (historyType !== 'subscription_wash') {
      return true;
    }
    const rowWashId = asNullableId(row?.subscription_wash_id ?? row?.history_id);
    return rowWashId !== washId;
  });
  const removed = rows.length - filtered.length;
  if (removed < 1) {
    return response;
  }

  const next: any = { ...response, data: filtered };

  if (typeof response.count === 'number' && Number.isFinite(response.count)) {
    next.count = Math.max(0, response.count - removed);
  }

  const pg = response.pagination;
  if (pg && typeof pg === 'object') {
    const totalRaw = Number(pg.total);
    const limitRaw = Number(pg.limit);
    const total = Number.isFinite(totalRaw) ? Math.max(0, totalRaw - removed) : totalRaw;
    const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : filtered.length || 1;
    next.pagination = {
      ...pg,
      total,
      totalPages: Number.isFinite(total)
        ? Math.max(1, Math.ceil(total / limit) || 1)
        : pg.totalPages,
    };
  }

  return next;
}

/** Strip one membership rewash candidate by subscription_wash id. */
export function omitMembershipRewashCandidate(
  eligibility: MembershipRewashEligibilityState | null,
  subscriptionWashId: string | null | undefined
): MembershipRewashEligibilityState | null {
  const washId = asNullableId(subscriptionWashId);
  if (!eligibility || !washId) {
    return eligibility;
  }
  const candidates = eligibility.candidates.filter(
    c => asNullableId(c.original_subscription_wash_id) !== washId
  );
  return {
    eligible: eligibility.eligible === true && candidates.length > 0,
    candidates,
  };
}

/**
 * Prefer an explicit wash id; else the newest subscription_wash row for a membership order.
 * History rows spread `o.*`, so `id` is the membership order UUID and `order_id` is the
 * display number (e.g. `#860…`) — match on UUID `id` only.
 */
export function resolveSubscriptionWashIdToOmit(params: {
  explicitWashId?: string | null;
  membershipOrderId?: string | null;
  historyRows?: any[] | null;
}): string | null {
  const explicit = asNullableId(params.explicitWashId);
  if (explicit) {
    return explicit;
  }

  const orderId = asNullableId(params.membershipOrderId);
  const rows = Array.isArray(params.historyRows) ? params.historyRows : [];
  if (!orderId || rows.length === 0) {
    return null;
  }

  const matches = rows
    .filter((row: any) => {
      if (String(row?.history_type ?? '').trim() !== 'subscription_wash') {
        return false;
      }
      // Prefer UUID `id` from o.*; display `order_id` (#…) must not win the match.
      const rowOrderId = asNullableId(row?.id);
      return rowOrderId === orderId;
    })
    .sort((a: any, b: any) => {
      const ta = new Date(a?.event_date || a?.wash_date || a?.created_at || 0).getTime();
      const tb = new Date(b?.event_date || b?.wash_date || b?.created_at || 0).getTime();
      return tb - ta;
    });

  return asNullableId(matches[0]?.subscription_wash_id ?? matches[0]?.history_id);
}

interface HistoryWashIdentity {
  historyType: string;
  historyId: string | null;
  subscriptionWashId: string | null;
  washTypeValue: string;
  orderStatus?: string | null;
  /** CWS-1882: set on a `prepaid_redemption` row; the wash's own id. */
  prepaidRedemptionId?: string | null;
  prepaidVisitId?: string | null;
}

function isRewashWashTypeValue(washTypeValue: string): boolean {
  return OrderUtils.isRewashWashType(washTypeValue);
}

function isScheduledOrderStatus(orderStatus: string | null | undefined): boolean {
  return (
    String(orderStatus ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_') === 'scheduled'
  );
}

function isMembershipOrPrepaidRedemption(row: HistoryWashIdentity): boolean {
  if (row.historyType === 'subscription_wash' || isPrepaidRedemptionHistoryType(row.historyType)) {
    return true;
  }
  return row.washTypeValue === 'prepaid_wash_redemption';
}

/**
 * Rows whose `order_status` belongs to a JOINed purchase order rather than to the wash on
 * the row: membership wash rows (the plan's order, often still `scheduled`) and, since
 * CWS-1882, prepaid visit rows (the book's sale, always `completed`). Neither status may
 * decide whether the wash can be rewashed.
 */
function inheritsPurchaseOrderStatus(row: HistoryWashIdentity): boolean {
  return row.historyType === 'subscription_wash' || isPrepaidRedemptionHistoryType(row.historyType);
}

/** Resolve create-rewash target for a history wash when it is currently eligible. */
export function resolveHistoryWashRewashTarget(
  row: HistoryWashIdentity,
  orderEligibility: RewashEligibilityState | null | undefined,
  membershipEligibility: MembershipRewashEligibilityState | null | undefined,
  prepaidEligibility?: PrepaidRewashEligibilityState | null
): OrderRequestRewashTarget | null {
  if (isRewashWashTypeValue(row.washTypeValue)) {
    return null;
  }
  // Subscription-wash history rows JOIN the membership *purchase* order (`o.*`), so
  // `order_status` is often still `scheduled` for unlimited memberships. That is not
  // the redemption lifecycle — do not block membership rewash on it (CWS-1208).
  // CWS-1882 gives prepaid visit rows the same shape, with the book sale's `completed`.
  if (!inheritsPurchaseOrderStatus(row) && isScheduledOrderStatus(row.orderStatus)) {
    return null;
  }

  if (
    row.historyType === 'subscription_wash' &&
    row.subscriptionWashId &&
    membershipEligibility?.eligible
  ) {
    const candidate = membershipEligibility.candidates.find(
      c => c.original_subscription_wash_id === row.subscriptionWashId
    );
    if (candidate) {
      return {
        originalSubscriptionWashId: candidate.original_subscription_wash_id,
        expiresAt: candidate.expires_at ?? null,
      };
    }
  }

  if (
    isPrepaidRedemptionHistoryType(row.historyType) &&
    row.prepaidRedemptionId &&
    prepaidEligibility?.eligible
  ) {
    const candidate = prepaidEligibility.candidates.find(
      c => c.original_prepaid_redemption_id === row.prepaidRedemptionId
    );
    if (candidate) {
      return {
        originalPrepaidRedemptionId: candidate.original_prepaid_redemption_id,
        expiresAt: candidate.expires_at ?? null,
      };
    }
  }

  if (
    !inheritsPurchaseOrderStatus(row) &&
    row.historyId &&
    orderEligibility?.eligible &&
    orderEligibility.original_order_id &&
    orderEligibility.original_order_id === row.historyId
  ) {
    return {
      originalOrderId: orderEligibility.original_order_id,
      expiresAt: orderEligibility.expires_at ?? null,
    };
  }

  return null;
}

export function classifyHistoryRowAction(
  row: HistoryWashIdentity,
  orderEligibility: RewashEligibilityState | null | undefined,
  membershipEligibility: MembershipRewashEligibilityState | null | undefined,
  prepaidEligibility?: PrepaidRewashEligibilityState | null
): OrderRequestHistoryActionKind {
  if (isRewashWashTypeValue(row.washTypeValue)) {
    return 'rewash_label';
  }
  if (
    resolveHistoryWashRewashTarget(row, orderEligibility, membershipEligibility, prepaidEligibility)
  ) {
    return 'rewash_button';
  }
  // Membership / prepaid redemptions before scheduled: subscription_wash rows inherit
  // the purchase order's `scheduled` status (see resolveHistoryWashRewashTarget).
  if (isMembershipOrPrepaidRedemption(row)) {
    return 'redeemed';
  }
  if (isScheduledOrderStatus(row.orderStatus)) {
    return 'scheduled';
  }
  return 'completed';
}

export function resolveHeaderRewashTarget(
  headerWash: OrderRequestHeaderWashRef | null | undefined,
  orderEligibility: RewashEligibilityState | null | undefined,
  membershipEligibility: MembershipRewashEligibilityState | null | undefined,
  prepaidEligibility?: PrepaidRewashEligibilityState | null
): OrderRequestRewashTarget | null {
  if (!headerWash) {
    return null;
  }
  return resolveHistoryWashRewashTarget(
    headerWash,
    orderEligibility,
    membershipEligibility,
    prepaidEligibility
  );
}

/**
 * POST /order/rewash takes exactly one of `original_order_id`, `original_subscription_wash_id`
 * and (CWS-1882) `original_prepaid_redemption_id`. Normalising here keeps every opener and
 * the reason modal agreeing on what a usable target is.
 */
export function normalizeRewashTarget(
  target: OrderRequestRewashTarget | null | undefined
): OrderRequestRewashTarget | null {
  const orderId = String(target?.originalOrderId ?? '').trim();
  const washId = String(target?.originalSubscriptionWashId ?? '').trim();
  const redemptionId = String(target?.originalPrepaidRedemptionId ?? '').trim();
  if ((orderId ? 1 : 0) + (washId ? 1 : 0) + (redemptionId ? 1 : 0) !== 1) {
    return null;
  }
  return {
    ...(orderId ? { originalOrderId: orderId } : {}),
    ...(washId ? { originalSubscriptionWashId: washId } : {}),
    ...(redemptionId ? { originalPrepaidRedemptionId: redemptionId } : {}),
    expiresAt: target?.expiresAt ?? null,
  };
}

export function cloneOrderRequestTodayBanner(
  banner: OrderRequestTodayBannerState
): OrderRequestTodayBannerState {
  return {
    ...banner,
    headerWash: banner.headerWash ? { ...banner.headerWash } : null,
    historyRows: banner.historyRows.map(row => ({ ...row })),
    historyPagination: banner.historyPagination ? { ...banner.historyPagination } : null,
  };
}

/** Same-day visit leads; the banner and the insight sentence both open with one. */
const USED_MEMBERSHIP_TODAY = 'This customer has already used a membership wash today.';
/** CWS-1882: a wash drawn off a book bought earlier is a visit, not a purchase made today. */
const USED_PREPAID_TODAY = 'This customer has already used a prepaid wash today.';
const PURCHASED_TODAY = 'This customer has already purchased one order today.';

/** Which of the three same-day leads a row gets. */
function todayVisitLead(row: any): string {
  if (isPrepaidRedemptionHistoryType(row?.history_type)) {
    return USED_PREPAID_TODAY;
  }
  return isMembershipRedemptionRow(row) ? USED_MEMBERSHIP_TODAY : PURCHASED_TODAY;
}

/**
 * CWS-1729: whether a banner sentence is the same-day visit warning rather than an
 * upsell. A redeemable DRB member's card keeps the warning and drops the upsell.
 */
export function isTodayVisitSuggestion(message: string | null | undefined): boolean {
  return (
    !!message &&
    (message.startsWith(USED_MEMBERSHIP_TODAY) ||
      message.startsWith(USED_PREPAID_TODAY) ||
      message.startsWith(PURCHASED_TODAY))
  );
}

export const EMPTY_HISTORY_WELCOME =
  'Welcome! Quick tip — if you plan to wash twice a month or more, the unlimited plan usually costs less. Want to start with that today?';

export function buildOrderRequestHistoryInsight(
  response: any,
  page: number,
  pageLimit: number
): OrderRequestHistoryInsightBuildResult {
  const rows = Array.isArray(response.data) ? response.data : [];
  if (rows.length === 0) {
    return { banner: null, suggestion: EMPTY_HISTORY_WELCOME };
  }

  const byEventDesc = (a: any, b: any) => {
    const ta = new Date(a?.event_date || a?.created_at || 0).getTime();
    const tb = new Date(b?.event_date || b?.created_at || 0).getTime();
    return tb - ta;
  };
  const sorted = [...rows].sort(byEventDesc);
  const mostRecent = sorted[0];

  const formatPackage = formatVehicleHistoryPackageName;
  const packageName = formatPackage(mostRecent);
  const lastVisit = formatInsightLastVisitDateTime(
    mostRecent.event_date || mostRecent.appointment_time || mostRecent.created_at
  );
  const washType = formatInsightWashTypeLabel(mostRecent);
  const totalVisits = Number(response.count ?? response.pagination?.total ?? rows.length);

  const ldp = response.last_defender_purchase;
  const hasDefender = ldp && typeof ldp === 'object';
  const defenderPackageName = hasDefender ? formatPackage(ldp) : '—';
  const defenderWashType = hasDefender
    ? formatWashTypeLabel((ldp.wash_type && String(ldp.wash_type)) || 'single_wash')
    : '—';
  const defenderDate = hasDefender
    ? formatInsightVisitDate(ldp.event_date || ldp.appointment_time || ldp.created_at)
    : '—';

  const historyRows = mapVehicleHistoryRows(sorted, formatPackage);
  const historyPagination = buildVehicleHistoryPagination(rows, response, page, pageLimit);

  const todayRows = getTodayVehicleHistoryRows(rows);
  const mostRecentToday = todayRows.length > 0 ? [...todayRows].sort(byEventDesc)[0] : null;
  const redeemedToday = !!mostRecentToday && isMembershipRedemptionRow(mostRecentToday);
  const prepaidRedeemedToday =
    !!mostRecentToday && isPrepaidRedemptionHistoryType(mostRecentToday.history_type);
  const todayLead = mostRecentToday ? todayVisitLead(mostRecentToday) : '';

  const banner: OrderRequestTodayBannerState = {
    insightLead: mostRecentToday
      ? `${todayLead} ${todayLead === PURCHASED_TODAY ? 'Order details:' : 'Visit details:'}`
      : 'Recent purchase activity for this vehicle. Order details:',
    packageName,
    lastVisit: lastVisit || '—',
    washType,
    totalVisits,
    hasDefenderRow: hasDefender,
    defenderPackageName: hasDefender ? defenderPackageName : '—',
    defenderWashType: hasDefender ? defenderWashType : '—',
    defenderDate: hasDefender ? defenderDate : '—',
    headerWash: buildHeaderWashRefFromApiRow(mostRecent),
    feedbackRating: parseFeedbackRating(mostRecent.feedback_rating),
    historyRows,
    historyPagination,
  };

  let suggestion = '';
  if (mostRecentToday) {
    const p = formatPackage(mostRecentToday);
    const w = formatInsightWashTypeLabel(mostRecentToday);
    if (prepaidRedeemedToday) {
      // No price: the wash came off a book bought earlier, and the joined purchase order's
      // `final_price` is what the BOOK cost, which is not what happened today.
      suggestion = `${USED_PREPAID_TODAY} Visit details, Book - ${w}, Wash - ${p}`;
    } else if (redeemedToday) {
      // No price: the wash came off a plan, and "$0.00" reads as a free sale.
      suggestion = `${USED_MEMBERSHIP_TODAY} Visit details, Plan - ${p}, Wash Type - ${w}`;
    } else {
      const price =
        mostRecentToday.final_price != null && mostRecentToday.final_price !== ''
          ? mostRecentToday.final_price
          : 'N/A';
      suggestion = `${PURCHASED_TODAY} Order details, Package - ${p}, Price - $${price}, Wash Type - ${w}`;
    }
  } else if (rows.length === 1) {
    suggestion = `Welcome back! Since you’ve washed with us before, just a heads up — if you come even twice a month, the unlimited plan usually saves money. Want to switch today?`;
  } else {
    suggestion = `Hey, I see you’ve been in a couple of times. At this point, the unlimited plan usually costs less than paying per wash. Want to upgrade today?`;
  }

  return { banner, suggestion };
}

export function patchOrderRequestHistoryBannerPage(
  banner: OrderRequestTodayBannerState,
  rows: any[],
  response: any,
  page: number,
  pageLimit: number
): OrderRequestTodayBannerState {
  const formatPackage = formatVehicleHistoryPackageName;
  const resolvedPage = Number(response.pagination?.page) || page;
  return {
    ...banner,
    historyRows: mapVehicleHistoryRows(rows, formatPackage),
    historyPagination: buildVehicleHistoryPagination(rows, response, resolvedPage, pageLimit),
  };
}
